import { useEffect, useState, useRef } from "react";
import Sidebar from "../../../sidebar/sidebar";
import Header from "../../../header/header";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";
import TabsComponent from "./dashboardComponent/TabsComponent/tabs";
import CreateGraphModal from './dashboardComponent/TabsComponent/GraphModal'
import AIDashboard from "../../../AIDashboard/AIDashboard/AIDashboard";
import BiDashboard from "./CustomDashboard/CustomDashboard";
import DashboardHeader from "./dashboardComponent/DashboardHeader";
import { useCurrentUser } from "../../../../hooks/useCurrentUser";
import { usePermission } from "../../../../hooks/usePermission";
import { FinancialYearProvider, useFinancialYear } from "../../../../context/FinancialYearContext";

const DashboardInner = (props) => {
  // 1. Hooks & Basic State
  const { userType, companyId } = useCurrentUser();
  // Global financial-year filter — single source of truth for every dashboard tab.
  const {
    financialYears: financialYear,
    selectedFYId: financialYearId,
    setSelectedFYId: setFinancialYearId,
  } = useFinancialYear();
  const [canViewEsgAtGlance] = usePermission("esg-at-glance.view", "ESG_AT_A_GLANCE", true);
  const [canViewDeviation] = usePermission("deviation.view", "DEVIATION", true);
  const [canViewIotMeters] = usePermission("iot-meters.view-list", "IOT_METERS", true);
  const [canViewCarbonSequestration] = usePermission("project.view", "CARBON_SEQUESTRATION", true);
  const isMounted = useRef(true);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);

  // 2. Data State
  const [graphSearchTerm, setGraphSearchTerm] = useState("");
  const [refreshKey, setRefreshKey] = useState(0); // 🔑 NEW: Controls auto-refresh
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [timePeriods, setTimePeriods] = useState({});
  const [locationOption, setLocationOption] = useState([]);
  const [framework, setFramework] = useState([]);
  const [keyTab, setKeyTab] = useState("combinedAll");
  // Default main tab: ESG at a glance (id: 17)

  // 3. API Data State
  const [graphData, setGraphData] = useState("");
  const [lastYearGraphData, setLastYearGraphData] = useState("");

  const [permissionGraph, setPermissionGraph] = useState([]);
  const [frameworkValue, setFrameworkValue] = useState([]);
  const [todaysActivities, setTodaysActivities] = useState([]);
  const [energyData, setEnergyData] = useState();
  const [energyTriggerData, setTiggerEnergyData] = useState();
  const [emissionTriggerData, setTiggerEmissionData] = useState();
  const [usersActivity, setUsersActivitys] = useState([]);

  // 4. Comparison State
  const [compareLastTimePeriods, setcompareLastTimePeriods] = useState({});
  const [compareTCurrentimePeriods, setcompareCurrentTimePeriods] = useState({});

  // 5. Modals State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openBiDashBoard, setOpenBiDashBoard] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);

  // --- TAB VISIBILITY LOGIC ---
  const isTabVisible = (tabName) => {
    if (!permissionGraph) return true;
    if (permissionGraph[tabName]?.length > 0) return true;
    if (["Progress", "Trainer Overview", "Trainee Overview", "Intensity"].includes(tabName)) return true;
    if (tabName === "Training") return true;
    return false;
  };

  const tabs = [
    { id: 17, label: "ESG at a glance", icon: "🌟", visible: (userType !== "TRAINEE" && canViewEsgAtGlance) },
    { id: 20, label: "Deviation", icon: "📉", visible: (userType !== "TRAINEE" && canViewDeviation) },
    { id: 18, label: "IOT Meters", icon: "📟", visible: (userType !== "TRAINEE" && canViewIotMeters) },
    { id: 19, label: "Carbon Sequestration", icon: "🌱", visible: (userType !== "TRAINEE" && canViewCarbonSequestration) },
    { id: 0, label: "Progress", icon: "📊", visible: userType !== "TRAINEE" },
    { id: 1, label: "Environment", icon: "🌱", visible: true },
    { id: 2, label: "Energy", icon: "⚡", visible: isTabVisible("Energy Management") },
    { id: 3, label: "Emission", icon: "☁️", visible: isTabVisible("Energy Management") },
    { id: 4, label: "Water", icon: "💧", visible: isTabVisible("Water Management") },
    { id: 5, label: "Waste", icon: "🗑️", visible: isTabVisible("Waste Module") },
    { id: 12, label: "Intensity", icon: "📈", visible: (userType !== "TRAINEE" && isTabVisible("Intensity") /* && frameworkValue?.some((f) => f.id === 48) */) },
    { id: 6, label: "Diversity", icon: "👥", visible: isTabVisible("Employee Diversity") },
    { id: 7, label: "Employment", icon: "💼", visible: isTabVisible("Employment") && frameworkValue?.some((f) => f.id === 48) },
    { id: 15, label: "Occupancy", icon: "🏢", visible: isTabVisible("Employment") && frameworkValue?.some((f) => f.id === 48) },
    { id: 8, label: "Health & Safety", icon: "🛡️", visible: isTabVisible("Health & Safety") },
    { id: 9, label: "Training", icon: "🎓", visible: (userType !== "TRAINEE" && isTabVisible("Training")) },
    { id: 10, label: "Trainer Overview", icon: "👨‍🏫", visible: (userType !== "TRAINEE" && String(companyId) !== "351") },
    { id: 11, label: "Trainee Overview", icon: "👨‍🎓", visible: String(companyId) !== "351" },
    { id: 16, label: "Governance", icon: "⚖️", visible: (userType !== "TRAINEE" && frameworkValue?.some((f) => f.id === 1)) },
  ];

  const visibleTabs = tabs.filter(tab => tab.visible);

  const getDefaultTab = () => {
    if (visibleTabs.find(t => t.id === 17)) {
      return 17;
    }
    return visibleTabs.length > 0 ? visibleTabs[0].id : null;
  };

  const [currentTab, setCurrentTab] = useState(getDefaultTab);

  // --- DATA SYNCHRONIZATION EFFECTS ---
  useEffect(() => {
    if (frameworkValue && frameworkValue.length > 0) {
      const initialFrameworks = frameworkValue.map((f) => ({ label: f.title, value: f.id }));
      setFramework(initialFrameworks);
    }
  }, [frameworkValue]);

  useEffect(() => {
    setTimePeriods(compareTCurrentimePeriods);
  }, [compareTCurrentimePeriods]);

  // --- API FETCHING FUNCTIONS ---
  const fetchFrameworkApi = async () => {
    const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getFramework`, {}, { type: "ALL", userId: JSON.parse(localStorage.getItem("user_temp_id")) });
    if (isSuccess && isMounted.current) setFrameworkValue(data?.data);
  };

  const getTotalTrainingData = async () => {
    if (financialYearId) {
      try {
        const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getTotalTrainingData`, {}, { financialYearId }, "GET");
        if (isSuccess && isMounted.current) {
          const updatedData = data?.data?.map((item) => {
            if (!item) return null;
            if (item.questionId === 301 || item.questionId === 310) {
              return {
                ...item,
                answer: item?.answer?.map((ans) => !isNaN(ans?.[0]) && ans?.[0] !== "No" ? [parseFloat(ans[0]) / 1000, ans[1]] : ans) ?? [],
              };
            }
            return item;
          }).filter(Boolean);
          setGraphData(updatedData);
        }
      } catch (err) { console.error(err); }
    }
  };

  const getPermissionGraphWithAssignedQuestions = async () => {
    if (financialYearId) {
      const endpoint = Number(companyId) === 331
        ? "getPermissionGraphWithAssignedQuestionsForBiData"
        : "getPermissionGraphWithAssignedQuestions";
      const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}${endpoint}`, {}, { financialYearId }, "GET");
      if (isSuccess) setPermissionGraph(data?.data);
    }
  };

  useEffect(() => {
    fetchFrameworkApi();
  }, []);

  useEffect(() => {
    getTotalTrainingData();
    getPermissionGraphWithAssignedQuestions();
  }, [financialYearId]);

  const handleModal = () => setIsModalOpen(!isModalOpen);
  const handleBiModal = (shouldRefresh = false) => {
    setIsModalOpen(false);
    setOpenBiDashBoard(!openBiDashBoard);

    if (shouldRefresh === true) {
      setRefreshKey(prev => prev + 1);
    }
  };
  const handleAiDashBoard = () => { setIsModalOpen(false); setIsAiModalOpen(!isAiModalOpen); };
  const downloadPdf = (type) => {
    const fileName = type === "PDF" ? "BRSR_Report_2026.pdf" : "BRSR_Report_2026.docx";
    const link = document.createElement("a");
    link.href = process.env.PUBLIC_URL + "/" + fileName;
    link.download = fileName;
    link.click();
    setShow(false);
  };

  // Dashboard.jsx
  const publishHandlersRef = useRef({});
  const handleRegisterPublishHandler = (module, fn) => {
    if (!module) return;

    if (fn) {
      publishHandlersRef.current[module] = fn;
    } else {
      delete publishHandlersRef.current[module];
    }
  };

  const handlePublishGraph = (graph) => {
    if (!graph?.visualization?.module) return;

    const module = graph.visualization.module;
    const handler = publishHandlersRef.current[module];

    if (typeof handler === "function") {
      handler(graph); // 🔥 routed to correct CustomGraph
    }
  };

  return (
    <div className="d-flex flex-row mainclass" style={{ height: "100vh", overflow: "auto" }}>
      <div style={{ flex: sidebarExpanded ? "0 0 21%" : "0 0 60px", position: "sticky", top: 0, zIndex: 999 }}>
        <Sidebar dataFromParent={props.location.pathname} onSidebarToggle={setSidebarExpanded} />
      </div>

      <div style={{ flex: "1", transition: "all 0.3s ease" }}>
        <div style={{ position: "sticky", top: 0, zIndex: 999 }}><Header /></div>

        <div className="main_wrapper" style={{ width: "100%" }}>
          <CreateGraphModal show={isModalOpen} onClose={handleModal} onCreateCustom={handleBiModal} onCreateWithAI={handleAiDashBoard} />

          <BiDashboard
            isOpen={openBiDashBoard}
            onClose={handleBiModal}
            onPublish={handlePublishGraph}
            fromDate={fromDate}
            toDate={toDate}
          />

          <AIDashboard isOpen={isAiModalOpen} onClose={handleAiDashBoard} />

          <div className="inner_wraapper" style={{ width: "100%", paddingTop: "10px" }}>
            <DashboardHeader
              tabs={tabs}
              graphSearchTerm={graphSearchTerm}
              setGraphSearchTerm={setGraphSearchTerm}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              handleModal={handleModal}
              setKeyTab={setKeyTab}
              keyTab={keyTab}
              framework={framework || []}
              frameworkValue={frameworkValue || []}
              setFramework={setFramework}
              setLocationOption={setLocationOption}
              setTimePeriods={setTimePeriods}
              setFinancialYearId={setFinancialYearId}
              setFromDate={setFromDate}
              setToDate={setToDate}
              financialYear={financialYear}
              show={show}
              handleClose={handleClose}
              downloadPdf={downloadPdf}
              setShow={setShow}
              financialYearId={financialYearId}
              setcompareLastTimePeriods={setcompareLastTimePeriods}
              setcompareCurrentTimePeriods={setcompareCurrentTimePeriods}
              compareLastTimePeriods={compareLastTimePeriods}
              compareTCurrentimePeriods={compareTCurrentimePeriods}
            />
          </div>

          <div className=" w-100 p-3">
            <TabsComponent
              activebtnTab={currentTab}
              refreshKey={refreshKey}
              graphSearchTerm={graphSearchTerm}
              permissionGraph={permissionGraph}
              keyTab={keyTab}
              framework={framework}
              frameworkValue={frameworkValue}
              fromDate={fromDate}
              toDate={toDate}
              financialYearId={financialYearId}
              locationOption={locationOption}
              timePeriods={timePeriods}
              graphData={graphData}
              todaysActivities={todaysActivities}
              compareLastTimePeriods={compareLastTimePeriods}
              compareTCurrentimePeriods={compareTCurrentimePeriods}
              energyData={energyData}
              usersActivity={usersActivity}
              financialYear={financialYear}
              registerPublishHandler={handleRegisterPublishHandler}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = (props) => (
  <FinancialYearProvider>
    <DashboardInner {...props} />
  </FinancialYearProvider>
);

export default Dashboard;
